import { faClose, faPlug } from "@fortawesome/free-solid-svg-icons"
import { useTranslation } from "react-i18next"
import Button, { ButtonIcon } from "../../../components/Button/Button"
import Animate, { AnimationTypes } from "../../../components/Animate/Animate"
import ModalPortal from "../../../components/Modal/ModalPortal"
import { TitlePageResponsiveIconSmall } from "../../../components/Titles/Titles"
import { PopupHelpButton } from "../../../components/Help/PopupHelpButton"
import InputField from "../../../components/InputField/InputField"
import GetSpace from "../../../utils/space"
import THEME from "../../../configuration/Theme"
import { SIZE } from "../../../configuration/Sizes"
import defaultStyle from "../../../styles/defaultPageStyle.module.css"

interface MetaPhoneRegistrationPopupProps {
  isOpen: boolean
  registerPin: string
  phoneRegisterStatus: string
  onClose: () => void
  onRegisterPinChange: (value: string) => void
  onRegisterPhoneNumber: () => void
  onCheckPhoneNumberStatus: () => void
}

export const MetaPhoneRegistrationPopup: React.FC<MetaPhoneRegistrationPopupProps> = ({
  isOpen,
  registerPin,
  phoneRegisterStatus,
  onClose,
  onRegisterPinChange,
  onRegisterPhoneNumber,
  onCheckPhoneNumberStatus,
}) => {
  const { t } = useTranslation("company")

  if (!isOpen) {
    return null
  }

  return (
    <ModalPortal>
      <div className={defaultStyle.overlay}>
        <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
          <div className={defaultStyle.alert}>
            <TitlePageResponsiveIconSmall
              title={t("label_meta_phone_registration_title")}
              iconColor={THEME.colors.iconBGColor}
              icon={faPlug}
            >
              <div className={`${defaultStyle.ButtonsActions} flex items-center gap-1`}>
                <PopupHelpButton
                  helpKey="pages.myrestaurant.mycompany.metaphoneregistrationpopup.1"
                  variant="global"
                  title={t("helpPopup:global.metaPhoneRegistration.title")}
                  description={t("helpPopup:global.metaPhoneRegistration.description")}
                  bullets={t("helpPopup:global.metaPhoneRegistration.bullets", { returnObjects: true }) as string[]}
                />

                <ButtonIcon
                  sizeNumber={SIZE.buttonCloseIconSize}
                  bgColor={THEME.colors.buttonBGCloseColor}
                  iconColor={THEME.colors.buttonFGCloseColor}
                  icon={faClose}
                  onClick={onClose}
                  size="small"
                  descriptionKey="interactionDescriptions:pages.myRestaurant.myCompany.metaPhoneRegistrationPopup.button.onClose"
                />
              </div>
            </TitlePageResponsiveIconSmall>

            <Animate type={AnimationTypes.fadeInTraslate} duration="1s">
              <div className="p-2 max-h-[70vh] overflow-y-auto">
                <section className="rounded border border-slate-300 bg-slate-50 p-3 text-xs text-slate-800">
                  <p className="font-semibold">{t("label_meta_phone_registration_intro_title")}</p>

                  <p className="mt-1">{t("label_meta_phone_registration_description")}</p>

                  <Animate type={AnimationTypes.fadeInTraslate} duration="1.2s">
                    <section className="mt-2 rounded border border-amber-300 bg-amber-50 p-2">
                      <p className="text-[11px] font-semibold text-amber-900">
                        {t("label_meta_phone_pin_optional_title")}
                      </p>
                      <ul className="mt-1 list-disc pl-4 space-y-1 text-[11px] text-amber-900">
                        <li>{t("label_meta_phone_pin_optional_line_1")}</li>
                        <li>{t("label_meta_phone_pin_optional_line_2")}</li>
                      </ul>
                    </section>
                  </Animate>

                  <Animate type={AnimationTypes.fadeInTraslate} duration="1.2s" delay="0.05s">
                    <section className="mt-2 rounded border border-slate-300 bg-white p-2">
                      <p className="text-[11px] font-semibold text-slate-900">{t("label_meta_phone_actions_title")}</p>
                      <ol className="mt-1 list-decimal pl-4 space-y-1 text-[11px] text-slate-800">
                        <li>{t("label_meta_phone_step_fill_pin")}</li>
                        <li>{t("label_meta_phone_step_register_number")}</li>
                        <li>{t("label_meta_phone_step_check_status")}</li>
                      </ol>
                    </section>
                  </Animate>

                  <GetSpace top={6} />

                  <InputField
                    type="text"
                    placeholder={t("label_meta_phone_registration_pin")}
                    value={registerPin}
                    onChange={(e) => onRegisterPinChange(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    descriptionKey="interactionDescriptions:pages.myRestaurant.myCompany.metaPhoneRegistrationPopup.input.metaPhoneRegistrationPin"
                  />

                  <p className="mt-1 text-[11px] text-slate-700">{t("label_meta_phone_registration_pin_hint")}</p>

                  <GetSpace top={6} />

                  <section className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    <Button
                      sizeNumber={SIZE.buttonCloseIconSize}
                      label={t("label_meta_phone_register_button")}
                      onClick={onRegisterPhoneNumber}
                      type="secondary"
                      size="small"
                      descriptionKey="interactionDescriptions:pages.myRestaurant.myCompany.metaPhoneRegistrationPopup.button.metaPhoneRegister"
                    />

                    <Button
                      sizeNumber={SIZE.buttonCloseIconSize}
                      label={t("label_meta_phone_check_status_button")}
                      onClick={onCheckPhoneNumberStatus}
                      type="primary"
                      size="small"
                      descriptionKey="interactionDescriptions:pages.myRestaurant.myCompany.metaPhoneRegistrationPopup.button.metaPhoneCheckStatus"
                    />
                  </section>

                  <p className="mt-2 text-[11px] text-slate-700">{t("label_meta_phone_actions_footer_hint")}</p>

                  {phoneRegisterStatus && (
                    <section className="mt-2 rounded border border-slate-300 bg-white p-2">
                      <p className="text-[11px] text-slate-900">
                        <strong>{t("label_meta_phone_status_title")}:</strong> {phoneRegisterStatus}
                      </p>
                    </section>
                  )}
                </section>
              </div>
            </Animate>
          </div>
        </Animate>
      </div>
    </ModalPortal>
  )
}
